import { PageCardContainer } from "@/components/shared/page-card/page-card-container";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TotalStatsContent } from "@/features/stats/containers/total-stats-content";
import { TypeStatsContent } from "@/features/stats/containers/type-stats-content";
import { getPageSummary, PAGE } from "@/pages";
import { capitalize, isEmpty } from "@banjoanton/utils";
import {
    ChessGameCollectionRepository,
    CollectionStatsRepository,
    STATS_VERSION,
    StatsService,
} from "@chesspresso/chess";
import { ChessGameRepository } from "@chesspresso/chess/src/db/chess-game-repository";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/(container)/stats")({
    component: RouteComponent,
    loader: async () => {
        const collections = await ChessGameCollectionRepository.getAll();

        if (isEmpty(collections)) {
            return { stats: undefined };
        }

        const collectionId = collections[0]?.id;

        if (!collectionId) {
            return { stats: undefined };
        }

        const cached = await CollectionStatsRepository.get(collectionId);
        const gameCount =
            await ChessGameRepository.countByCollection(collectionId);

        if (
            cached &&
            cached.version === STATS_VERSION &&
            cached.totalGames === gameCount
        ) {
            return { stats: cached };
        }

        const games = await ChessGameRepository.getByCollection(collectionId);
        const stats = StatsService.calculateStats(games, collectionId);
        await CollectionStatsRepository.save(stats);

        return { stats };
    },
});

const page = getPageSummary(PAGE.Stats);

function RouteComponent() {
    const { stats } = Route.useLoaderData();
    const navigate = useNavigate();

    const timeClasses = stats?.statsByType.map((s) => s.type) ?? [];
    const [selectedType, setSelectedType] = useState("total");

    if (!stats) {
        return (
            <PageCardContainer title={page.title} Icon={page.icon}>
                <Card>
                    <CardHeader className="items-center">
                        <CardTitle className="text-center">
                            No stats yet
                        </CardTitle>
                        <CardDescription className="text-center mb-4">
                            Import your games first to see your stats.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex justify-center">
                        <button
                            type="button"
                            className="cursor-pointer"
                            onClick={() => navigate({ to: "/import" })}
                        >
                            Import games
                        </button>
                    </CardContent>
                </Card>
            </PageCardContainer>
        );
    }

    const statsToShow = stats.statsByType.find((s) => s.type === selectedType);

    return (
        <PageCardContainer
            title={page.title}
            Icon={page.icon}
            description="Detailed breakdown of your chess performance"
        >
            <Tabs
                value={selectedType}
                onValueChange={setSelectedType}
                className="mb-6"
            >
                <TabsList>
                    <TabsTrigger value="total">Total</TabsTrigger>
                    {timeClasses.map((type) => (
                        <TabsTrigger key={type} value={type}>
                            {capitalize(type)}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            {selectedType === "total" ? (
                <TotalStatsContent stats={stats} />
            ) : null}
            {selectedType !== "total" && statsToShow ? (
                <TypeStatsContent statsToShow={statsToShow} />
            ) : null}
        </PageCardContainer>
    );
}
